class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* ============================================================
   Universal Cart Refresh Bridge — Sense-Nature
   Any section/app/block can refresh the drawer with ONE line:
     document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
   or call window.senseRefreshCart({ open: true }).
   Re-renders the cart-drawer section ONCE (locale-aware) and surgically
   replaces only the dynamic inner nodes, leaving the customised outer
   drawer and all custom CSS untouched. Guarded against concurrent runs.
   ============================================================ */
(function () {
  if (window.__senseCartRefreshBound) return;
  window.__senseCartRefreshBound = true;
  var inFlight = false;

  function getRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
           (typeof routes !== 'undefined' && routes.root) || '/';
  }

  function senseRefreshCart(options) {
    options = options || {};
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || inFlight) return Promise.resolve();
    inFlight = true;

    return fetch(getRoot() + '?sections=cart-drawer,cart-icon-bubble')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data && data['cart-drawer']) {
          var doc = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
          // Surgical replace of dynamic inner nodes only.
          ['cart-drawer-items', '.cart-drawer__footer', '.luxury-shipping-container'].forEach(function (sel) {
            var target = document.querySelector(sel);
            var source = doc.querySelector(sel);
            if (target && source) target.replaceWith(source);
          });
          // Determine empty/filled state from the fresh server render.
          var fresh = doc.querySelector('cart-drawer');
          var isEmpty = fresh ? fresh.classList.contains('is-empty') : !doc.querySelector('.cart-item');
          // Toggle is-empty on the outer wrapper and .drawer__inner.
          drawer.classList.toggle('is-empty', isEmpty);
          var inner = drawer.querySelector('.drawer__inner');
          if (inner) inner.classList.toggle('is-empty', isEmpty);
          // Explicitly resolve child visibility that CSS class-toggling alone
          // cannot reliably fix in this custom Dawn variant: when the cart was
          // empty at page-load, Liquid physically rendered .drawer__inner-empty
          // into the DOM.  Removing is-empty from cart-drawer hides it via CSS
          // in vanilla Dawn, but custom theme overrides can leave it visible.
          // Force the correct display state directly on the nodes.
          if (!isEmpty) {
            var emptyNode = drawer.querySelector('.drawer__inner-empty');
            if (emptyNode) emptyNode.style.display = 'none';
            var contents = drawer.querySelector('.drawer__contents');
            if (contents) contents.style.display = '';
            var cartFooter = drawer.querySelector('.cart-drawer__footer');
            if (cartFooter) cartFooter.style.display = '';
          } else {
            var emptyNodeShow = drawer.querySelector('.drawer__inner-empty');
            if (emptyNodeShow) emptyNodeShow.style.display = '';
          }
        }
        if (data && data['cart-icon-bubble']) {
          var bDoc = new DOMParser().parseFromString(data['cart-icon-bubble'], 'text/html');
          var bSrc = bDoc.querySelector('.shopify-section') || bDoc.body;
          var bTgt = document.querySelector('#cart-icon-bubble');
          if (bTgt && bSrc) bTgt.innerHTML = bSrc.innerHTML;
        }
        if (options.open !== false && typeof drawer.open === 'function') drawer.open();
      })
      .catch(function (e) { console.error('[Sense cart refresh]', e); })
      .finally(function () { inFlight = false; });
  }

  window.senseRefreshCart = senseRefreshCart;
  document.addEventListener('cart:refresh', function (evt) {
    senseRefreshCart((evt && evt.detail) || {});
  });
})();
